import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, Check, ChevronLeft, ChevronRight, RotateCcw, Undo2, X } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Badge } from "../../components/Badge";
import type { BadgeTone } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { useAuth } from "../auth/useAuth";
import { LEAVE_STATUS_LABEL, LEAVE_TYPE_LABEL } from "../../api/types";
import type { LeaveBalanceRow, LeaveRequest, LeaveStatus } from "../../api/types";
import { taipeiToday } from "../attendance/time";
import { LeaveBalance, formatLeaveDays } from "./LeaveBalance";
import { LeaveCalendar, formatLeaveRange } from "./LeaveCalendar";
import { LeaveDialog } from "./LeaveDialog";

// 請假(第七輪 Task 3)。入口在側邊欄「設定」上方,業主指定的位置。
//
// --------------------------------------------------------------------------
// 這一頁由上而下是一句話:我還剩幾天 → 那幾天公司在幹嘛 → 誰請了什麼 →(老闆)誰在等我
// --------------------------------------------------------------------------
// 日曆擺在清單之前,是因為業主要的就是那個順序:「其實也是跟大家說哪一天公司有
// 什麼事情,你確定你要休假嗎 的意思」—— 先看見公司那幾天有事,再決定要不要請。
//
// --------------------------------------------------------------------------
// 誰看得到什麼
// --------------------------------------------------------------------------
// **所有人看得到所有人的假。** 這不是疏漏,是業主要的周知。真正分權的只有
// 「裁決」那一段:只有老闆與被指派「假單核准」的人看得到待核准清單與准 / 駁回兩顆鈕,後端
// (app/reviewers.py)再擋一次 —— 畫面上藏起來不等於擋住,兩層都要有。

/** 狀態徽章的色調。文字一律由 LEAVE_STATUS_LABEL 提供,不可只靠顏色。 */
const STATUS_TONE: Record<LeaveStatus, BadgeTone> = {
  PENDING: "warn",
  APPROVED: "ok",
  REJECTED: "danger",
  CANCELLED: "neutral",
};

const HEADERS = ["姓名", "假別", "日期", "天數", "狀態", "說明", "裁決", "動作"];

/** 這一筆是不是本人、而且還沒被裁決 —— 只有這種假單撤得回來。 */
function canCancel(row: LeaveRequest, userId: number | undefined): boolean {
  return row.status === "PENDING" && userId !== undefined && row.user_id === userId;
}

/**
 * 這一筆是不是本人已核准、而且還沒開始(起日晚於台北的今天)——
 * 申請人可以自己取消。後端再擋一次(409),畫面藏鈕不等於擋住。
 */
function canSelfCancelApproved(row: LeaveRequest, userId: number | undefined): boolean {
  return (
    row.status === "APPROVED" &&
    userId !== undefined &&
    row.user_id === userId &&
    row.start_date > taipeiToday()
  );
}

/**
 * 裁決那一欄怎麼稱呼這一筆的處理。沒有人裁決過就回 null,整格留白。
 *
 * 撤銷核准也是老闆做的事,一樣要看得到是誰撤的、寫了什麼原因 —— 只認
 * APPROVED / REJECTED 的話,被撤銷的人在畫面上永遠看不到那句話。
 * 申請人自己撤回的那一種沒有裁決者(decided_by 是 null),這裡就回 null。
 */
/** 今年,以**本地時間**判定 —— 使用者說的「今年」是他日曆上的那一年。 */
function currentYear(): number {
  return new Date().getFullYear();
}

function decisionLabel(row: LeaveRequest): string | null {
  if (row.status === "APPROVED") return "核准";
  if (row.status === "REJECTED") return "駁回";
  if (row.status === "CANCELLED" && row.decided_by !== null) {
    // 申請人自己取消已核准的假,後端把裁決者記成申請人本人;老闆撤銷則是老闆
    return row.decided_by === row.user_id ? "申請人取消" : "撤銷核准";
  }
  return null;
}

export function LeavePage() {
  const { user } = useAuth();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const isOwner = user?.role === "OWNER";

  // 清單與額度看哪一年。**一定要切得動。**
  //
  // 寫死今年的話,12 月申請隔年 1 月的假(過年前最常見的操作)整段落在下一年:
  // 申請人的表格沒有那一列(撤回鈕在列上,所以撤不回),老闆的「待核准」區塊
  // 也沒有那一筆(准 / 駁回兩顆鈕只存在於那一區)。那張假單就卡在待核准,
  // 畫面上沒有任何路徑處理得了 —— 日曆翻得過去,但日曆上的那一格只有文字。
  //
  // 這裡**不跟著日曆的游標走**:翻去看明年的檔期時,下面的清單不該整個換掉。
  // 兩者各自獨立,要換年度就按年度那兩顆鈕。
  const [year, setYear] = useState(currentYear);

  const [dialogOpen, setDialogOpen] = useState(false);
  /** 正在填駁回原因的那一筆。null 代表沒有人在駁回。 */
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  /** 正在撤銷核准的那一筆。撤銷會退回特休額度,所以一定先問一次。 */
  const [revoking, setRevoking] = useState<LeaveRequest | null>(null);
  const [revokeNote, setRevokeNote] = useState("");
  /** 申請人正要自己取消的那一筆已核准的假。取消會退回特休,所以先問一次。 */
  const [selfCancelling, setSelfCancelling] = useState<LeaveRequest | null>(null);

  const leaveQuery = useQuery({
    queryKey: ["leave", year],
    queryFn: () => api.get<LeaveRequest[]>("/leave", { year }),
  });

  const balanceQuery = useQuery({
    queryKey: ["leave-balance", year],
    queryFn: () => api.get<LeaveBalanceRow[]>("/leave/balance", { year }),
  });

  const rows = Array.isArray(leaveQuery.data) ? leaveQuery.data : [];
  const balances = Array.isArray(balanceQuery.data) ? balanceQuery.data : [];
  const myBalance = balances.find((row) => row.user_id === user?.id) ?? null;
  // 覆核指派(業主 2026-11-02):老闆,或被指派「假單核准」的人。被指派的人不核自己的假。
  const rightsQuery = useQuery({
    queryKey: ["reviewers-mine"],
    queryFn: () => api.get<{ kinds: string[] }>("/reviewers/mine"),
  });
  const canReviewLeave = isOwner || (rightsQuery.data?.kinds ?? []).includes("LEAVE");
  const canDecide = (row: LeaveRequest) => isOwner || (canReviewLeave && row.user_id !== user?.id);
  const pending = rows.filter((row) => row.status === "PENDING" && canDecide(row));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["leave"] });
    void queryClient.invalidateQueries({ queryKey: ["leave-balance"] });
  };

  const decide = useMutation({
    mutationFn: (input: { id: number; approve: boolean; note: string }) =>
      api.post<LeaveRequest>(`/leave/${input.id}/decide`, { approve: input.approve, note: input.note }),
    onSuccess: (_row, input) => {
      refresh();
      setRejecting(null);
      setRejectNote("");
      push(input.approve ? "已核准這張假單" : "已駁回這張假單", "success");
    },
    onError: (err) => push(err instanceof ApiError ? err.message : "裁決失敗,請稍後再試。", "error"),
  });

  const revoke = useMutation({
    // 核准之後臨時不休(專案插件、家人不能去了)唯一的回頭路。改成 CANCELLED,
    // 那幾天的特休就退回額度裡 —— 後端的 balance 只算已核准的特休。
    mutationFn: (input: { id: number; note: string }) =>
      api.post<LeaveRequest>(`/leave/${input.id}/revoke`, { note: input.note }),
    onSuccess: () => {
      refresh();
      setRevoking(null);
      setRevokeNote("");
      push("已撤銷這張假單的核准,那幾天的特休退回去了", "success");
    },
    onError: (err) => push(err instanceof ApiError ? err.message : "撤銷失敗,請稍後再試。", "error"),
  });

  const cancel = useMutation({
    // 後端改成 CANCELLED 而不是真的刪掉:那幾天到底有沒有人請過假、
    // 後來是誰收回的,是排班爭議唯一查得到的痕跡。
    mutationFn: (id: number) => api.del(`/leave/${id}`),
    onSuccess: () => {
      refresh();
      push("已撤回這張假單", "success");
    },
    onError: (err) => push(err instanceof ApiError ? err.message : "撤回失敗,請稍後再試。", "error"),
  });

  const selfCancel = useMutation({
    // 與撤回同一支 DELETE:後端依狀態分流(待核准 → 撤回;已核准且沒開始 → 取消)
    mutationFn: (id: number) => api.del(`/leave/${id}`),
    onSuccess: () => {
      refresh();
      setSelfCancelling(null);
      push("已取消這張假單", "success");
    },
    onError: (err) => {
      setSelfCancelling(null);
      push(err instanceof ApiError ? err.message : "取消失敗,請稍後再試。", "error");
    },
  });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-xl font-semibold text-[var(--text)]">請假</h2>
          {/* 年度切換。數字本身寫出來,不可以只有兩個箭頭讓人自己數。 */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="上一年"
              onClick={() => setYear((prev) => prev - 1)}
              className="rounded-[var(--radius)] p-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              <ChevronLeft aria-hidden="true" size={18} />
            </button>
            <span className="tabular min-w-[4.5rem] text-center text-base font-semibold text-[var(--text)]">
              {`${year} 年`}
            </span>
            <button
              type="button"
              aria-label="下一年"
              onClick={() => setYear((prev) => prev + 1)}
              className="rounded-[var(--radius)] p-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              <ChevronRight aria-hidden="true" size={18} />
            </button>
            {year === currentYear() ? null : (
              <button
                type="button"
                onClick={() => setYear(currentYear())}
                className="ml-1 rounded-[var(--radius)] px-2 py-1 text-xs text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
              >
                回到今年
              </button>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setDialogOpen(true)}
          icon={<CalendarPlus aria-hidden="true" size={16} />}
        >
          申請休假
        </Button>
      </div>
      <p className="m-0 text-sm text-[var(--text-2)]">
        日曆上同時有同仁的假與報價單的專案檔期,排假之前先看看那幾天公司有什麼事。
        送出後是「待核准」,由老闆決定准或駁回。
        下面的清單、待核准與特休額度都是{`${year}`} 年的;跨年的假(例如 12/30 請到 1/2)兩年都看得到。
      </p>

      <LeaveBalance row={myBalance} loading={balanceQuery.isLoading} />

      <LeaveCalendar />

      {/* 待核准只有能核准的人看得到(老闆與被指派的人),而且沒有人在等的時候整段不出現 ——
          空的「待核准(0)」只是佔著位置,還讓人多看一眼才知道沒事。 */}
      {canReviewLeave && pending.length > 0 ? (
        <section
          aria-label="待核准的假單"
          className="flex min-w-0 flex-col gap-2 rounded-[var(--radius)] border border-[var(--warn)] bg-[var(--surface)] p-3"
        >
          <h3 className="m-0 text-sm font-semibold text-[var(--text)]">{`待核准(${pending.length})`}</h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {pending.map((row) => {
              const who = `${row.user_name} ${LEAVE_TYPE_LABEL[row.leave_type]} ${formatLeaveRange(row)}`;
              return (
                <li
                  key={row.id}
                  className="flex flex-col gap-2 border-t border-[var(--border)] pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <span className="font-medium text-[var(--text)]">{row.user_name}</span>
                    <span className="text-[var(--text-2)]">{LEAVE_TYPE_LABEL[row.leave_type]}</span>
                    <span className="tabular text-[var(--text-2)]">{formatLeaveRange(row)}</span>
                    <span className="tabular text-[var(--text-2)]">{`${formatLeaveDays(row.days)} 天`}</span>
                    {row.reason === "" ? null : <span className="text-[var(--text-3)]">{row.reason}</span>}
                    <span className="ml-auto flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        aria-label={`准 ${who}`}
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: row.id, approve: true, note: "" })}
                        icon={<Check aria-hidden="true" size={14} />}
                      >
                        准
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={`駁回 ${who}`}
                        onClick={() => {
                          setRejecting(row.id);
                          setRejectNote("");
                        }}
                        icon={<X aria-hidden="true" size={14} />}
                      >
                        駁回
                      </Button>
                    </span>
                  </div>

                  {/* 駁回的原因是被駁回的人最需要知道的東西(業主:「可寫原因」),
                      所以按了駁回先問一句,而不是直接打回票。 */}
                  {rejecting === row.id ? (
                    <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius)] bg-[var(--surface-2)] p-2">
                      <div className="min-w-0 flex-1">
                        <Input
                          label="駁回原因"
                          name="decision_note"
                          value={rejectNote}
                          hint="可留白,但寫一句話對方才知道為什麼。"
                          onChange={(event) => setRejectNote(event.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: row.id, approve: false, note: rejectNote })}
                      >
                        確認駁回
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setRejecting(null)}>
                        取消
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {leaveQuery.isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {leaveQuery.error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          假單清單載入失敗,請稍後再試。
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="假單清單" className="w-full min-w-[48rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {HEADERS.map((header) => (
                  <th key={header} scope="col" className="px-3 py-2">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 text-sm text-[var(--text)]">{row.user_name}</td>
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">{LEAVE_TYPE_LABEL[row.leave_type]}</td>
                  <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{formatLeaveRange(row)}</td>
                  <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{`${formatLeaveDays(row.days)} 天`}</td>
                  <td className="px-3 py-2 text-sm">
                    <Badge tone={STATUS_TONE[row.status]}>{LEAVE_STATUS_LABEL[row.status]}</Badge>
                  </td>
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">{row.reason}</td>
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">
                    {decisionLabel(row) !== null ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-[var(--text-3)]">
                          {`${row.decider_name} ${decisionLabel(row)}`}
                        </span>
                        {row.decision_note === "" ? null : <span>{row.decision_note}</span>}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {canCancel(row, user?.id) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`撤回 ${LEAVE_TYPE_LABEL[row.leave_type]} ${formatLeaveRange(row)}`}
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(row.id)}
                        icon={<Undo2 aria-hidden="true" size={14} />}
                      >
                        撤回
                      </Button>
                    ) : null}
                    {canSelfCancelApproved(row, user?.id) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`取消假單 ${LEAVE_TYPE_LABEL[row.leave_type]} ${formatLeaveRange(row)}`}
                        disabled={selfCancel.isPending}
                        onClick={() => setSelfCancelling(row)}
                        icon={<Undo2 aria-hidden="true" size={14} />}
                      >
                        取消
                      </Button>
                    ) : null}
                    {/* 核准之後臨時不休,唯一救得回額度的路。老闆與被指派的人 ——
                        後端再擋一次,畫面藏起來不等於擋住。 */}
                    {canDecide(row) && row.status === "APPROVED" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`撤銷核准 ${row.user_name} ${LEAVE_TYPE_LABEL[row.leave_type]} ${formatLeaveRange(row)}`}
                        disabled={revoke.isPending}
                        onClick={() => {
                          setRevoking(row);
                          setRevokeNote("");
                        }}
                        icon={<RotateCcw aria-hidden="true" size={14} />}
                      >
                        撤銷核准
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* 標題那一句要單獨留在一個元素裡:年度是可切換的,測試與螢幕閱讀器
          都照「{年} 年還沒有任何假單。」這一整句抓。 */}
      {!leaveQuery.isLoading && !leaveQuery.error && rows.length === 0 ? (
        <EmptyState
          title={`${year} 年還沒有任何假單。`}
          description="用上面的「申請休假」送出第一張;送出後由老闆核准。"
        />
      ) : null}

      {/* 撤銷核准會動到別人已經排好的那幾天,所以一定先問一次,而且把後果講清楚。 */}
      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title="撤銷核准"
        maxWidth="max-w-md"
        closeOnBackdrop={false}
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-[var(--text-2)]">
            {revoking === null
              ? ""
              : `要撤銷「${revoking.user_name} ${LEAVE_TYPE_LABEL[revoking.leave_type]} ${formatLeaveRange(revoking)}」的核准嗎?` +
                `那一筆會變成已取消,${formatLeaveDays(revoking.days)} 天的特休會退回去,那幾天也可以重新申請。`}
          </p>
          <Input
            label="撤銷原因"
            name="revoke_note"
            value={revokeNote}
            hint="可留白,但寫一句話對方才知道為什麼 —— 那幾天他原本已經安排好了。"
            onChange={(event) => setRevokeNote(event.target.value)}
          />
          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setRevoking(null)} disabled={revoke.isPending}>
              取消
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={revoke.isPending}
              onClick={() => {
                if (revoking === null) return;
                revoke.mutate({ id: revoking.id, note: revokeNote });
              }}
            >
              確認撤銷
            </Button>
          </div>
        </div>
      </Dialog>

      {/* 申請人自己取消已核准的假:先問一次,把後果(特休退回、日子可重請)講清楚。 */}
      <Dialog
        open={selfCancelling !== null}
        onClose={() => setSelfCancelling(null)}
        title="取消假單"
        maxWidth="max-w-md"
        closeOnBackdrop={false}
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-[var(--text-2)]">
            {selfCancelling === null
              ? ""
              : `要取消「${LEAVE_TYPE_LABEL[selfCancelling.leave_type]} ${formatLeaveRange(selfCancelling)}」嗎?` +
                `取消後${
                  selfCancelling.leave_type === "ANNUAL" ? ` ${formatLeaveDays(selfCancelling.days)} 天的特休會退回,` : ""
                }那幾天也可以重新申請。`}
          </p>
          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setSelfCancelling(null)} disabled={selfCancel.isPending}>
              先不要
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={selfCancel.isPending}
              onClick={() => {
                if (selfCancelling === null) return;
                selfCancel.mutate(selfCancelling.id);
              }}
            >
              確認取消
            </Button>
          </div>
        </div>
      </Dialog>

      <LeaveDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
